import { Badge } from 'src/design/atoms/Badge';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import React from 'react';
import { formatDate } from 'src/utils';
import { StepProps } from './types';

export interface SubmitStepProps extends StepProps {
  /** Progress-managed accounts with installed quantity from the Progress viewer. */
  designProgressAccountCount?: number;
}

export const SubmitStep: React.FC<SubmitStepProps> = ({
  values,
  selectedJob,
  designProgressAccountCount = 0,
}) => {
  const getProgressSummary = () => {
    if (values.excludeProgress) return 'No progress recorded';
    const itemsWithProgress = values.progressItemRequests.filter(
      (item: any) => item.value > 0,
    );
    const recorded = itemsWithProgress.length + designProgressAccountCount;
    return recorded > 0
      ? `${recorded} progress item(s) recorded`
      : 'No progress items with values';
  };

  const getTimesheetSummary = () => {
    if (values.excludeTimesheets) return 'No timesheets recorded';
    // This would need to be calculated based on actual timesheet data
    return 'Timesheet data included';
  };

  const getQuestionsSummary = () => {
    if (values.excludeQuestions) return 'No questions answered';
    const answeredQuestions = values.questions.filter(
      (q: any) => q.note || q.value !== 'yes',
    );
    return `${answeredQuestions.length} question(s) answered`;
  };

  const getPhotosSummary = () => {
    if (values.excludeImages) return 'No photos uploaded';
    const totalPhotos =
      (values.attachment?.length || 0) + (values.attachmentsToAdd?.length || 0);
    return totalPhotos > 0
      ? `${totalPhotos} photo(s) included`
      : 'No photos uploaded';
  };

  return (
    <>
      <Title order={3}>Review Daily Report</Title>
      <Text c="dimmed">
        Please review the information below before submitting your daily report.
        You can go back to any previous step to make changes.
      </Text>

      <Paper p="md" withBorder>
        <Stack gap="sm">
          <Group justify="space-between">
            <Text fw={500}>Job:</Text>
            <Text>
              {selectedJob
                ? `${selectedJob.jobNumber} - ${selectedJob.name}`
                : 'Not selected'}
            </Text>
          </Group>

          <Group justify="space-between">
            <Text fw={500}>Date:</Text>
            <Text>{formatDate(values.date)}</Text>
          </Group>

          <Group justify="space-between">
            <Text fw={500}>Weather:</Text>
            <Text>{values.weather || 'Not specified'}</Text>
          </Group>

          <Box>
            <Text fw={500} mb="xs">
              Description:
            </Text>
            <Text
              size="sm"
              c="dimmed"
              style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
            >
              {values.description || 'No description provided'}
            </Text>
          </Box>
        </Stack>
      </Paper>

      <Paper p="md" withBorder>
        <Title order={4} mb="sm">
          Summary
        </Title>
        <Stack gap="xs">
          <Group justify="space-between">
            <Text>Progress:</Text>
            <Badge
              color={values.excludeProgress ? 'gray' : 'blue'}
              variant="light"
            >
              {getProgressSummary()}
            </Badge>
          </Group>

          <Group justify="space-between">
            <Text>Timesheets:</Text>
            <Badge
              color={values.excludeTimesheets ? 'gray' : 'blue'}
              variant="light"
            >
              {getTimesheetSummary()}
            </Badge>
          </Group>

          <Group justify="space-between">
            <Text>Questions:</Text>
            <Badge
              color={values.excludeQuestions ? 'gray' : 'blue'}
              variant="light"
            >
              {getQuestionsSummary()}
            </Badge>
          </Group>

          <Group justify="space-between">
            <Text>Photos:</Text>
            <Badge
              color={values.excludeImages ? 'gray' : 'blue'}
              variant="light"
            >
              {getPhotosSummary()}
            </Badge>
          </Group>
        </Stack>
      </Paper>
    </>
  );
};
